import { cn } from '@/lib/utils';
import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { router } from 'router';

import { useAuth, useChatSession, useConfig } from '@chainlit/react-client';

import ChatSettingsModal from './components/ChatSettings';
import { ThemeProvider } from './components/ThemeProvider';
import { Loader } from '@/components/Loader';
import { Toaster } from '@/components/ui/sonner';

import { userEnvState } from 'state/user';

declare global {
  interface Window {
    cl_shadowRootElement?: HTMLDivElement;
    transports?: string[];
    theme?: {
      light: Record<string, string>;
      dark: Record<string, string>;
    };
  }
}

function App() {
  const { config } = useConfig();

  const { isAuthenticated, data, isReady } = useAuth();
  const userEnv = useRecoilValue(userEnvState);
  const { connect, disconnect, chatProfile, setChatProfile, idToResume } =
    useChatSession();

  const configLoaded = !!config;

  const chatProfileOk = configLoaded
    ? config.chatProfiles.length
      ? !!chatProfile
      : true
    : false;

  useEffect(() => {
    if (!isAuthenticated || !isReady || !chatProfileOk) {
      return;
    }

    // On reload at /thread/:id, AutoResumeThread sets idToResume from the URL,
    // but that happens after this effect first runs — so the initial connect
    // would go out with threadId=''. Chainlit's restore_existing_session keys
    // off sessionId and discards the threadId on the follow-up connect, so the
    // first one wins and on_chat_start fires → user lands in a fresh thread.
    // Gate the first connect until idToResume catches up.
    const threadMatch = window.location.pathname.match(/^\/thread\/([^/]+)/);
    if (threadMatch && idToResume !== threadMatch[1]) {
      return;
    }

    connect({
      transports: window.transports,
      userEnv
    });
  }, [userEnv, isAuthenticated, connect, isReady, chatProfileOk, idToResume]);

  // Force a fresh socket handshake when the tab comes back to the foreground
  // or the network comes back online. iOS/macOS aggressively throttle idle
  // websockets; without this, socket.io sits in exponential backoff against a
  // stale sticky cookie and "cannot reach server" surfaces until backoff
  // eventually catches up (or the user refreshes).
  useEffect(() => {
    if (!isAuthenticated || !isReady || !chatProfileOk) return;

    const forceReconnect = () => {
      disconnect();
      connect({ transports: window.transports, userEnv });
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') forceReconnect();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', forceReconnect);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', forceReconnect);
    };
  }, [isAuthenticated, isReady, chatProfileOk, connect, disconnect, userEnv]);

  useEffect(() => {
    if (
      !configLoaded ||
      !config ||
      !config.chatProfiles?.length ||
      chatProfile
    ) {
      return;
    }

    const defaultChatProfile = config.chatProfiles.find(
      (profile) => profile.default
    );

    if (defaultChatProfile) {
      setChatProfile(defaultChatProfile.name);
    } else {
      setChatProfile(config.chatProfiles[0].name);
    }
  }, [configLoaded, config, chatProfile, setChatProfile]);

  if (!configLoaded && isAuthenticated) return null;

  return (
    <ThemeProvider
      storageKey="vite-ui-theme"
      defaultTheme={data?.default_theme}
    >
      <Toaster richColors className="toast" position="top-right" />

      <ChatSettingsModal />
      <RouterProvider router={router} />

      <div
        className={cn(
          'bg-[hsl(var(--background))] flex items-center justify-center fixed size-full p-2 top-0',
          isReady && 'hidden'
        )}
      >
        <Loader className="!size-6" />
      </div>
    </ThemeProvider>
  );
}

export default App;
